<template>
    <div class="manage-pm-modal">
        <header class="mpm-header">
            <div class="mpm-title">
                <span class="mpm-icon"><CreditCardIcon class="w-5 h-5 text-gray-600" /></span>
                <div>
                    <p class="mpm-heading">Payment methods</p>
                    <p class="mpm-sub">Your primary card is charged first; backups are tried automatically if it fails.</p>
                </div>
            </div>
            <button type="button" class="mpm-close" aria-label="Close" @click="closeDialog">
                <XMarkIcon class="w-5 h-5" />
            </button>
        </header>

        <div class="mpm-body">
            <PaymentMethodsPanel
                embedded
                mode="settings"
                :methods="sortedMethods"
                :at-cap="atCap"
                :busy="busy"
                :hide-add="adding || !!editingId"
                @set-primary="onSetPrimary"
                @edit="onEdit"
                @delete="onDelete"
                @add="onAdd"
            />

            <!-- Inline edit (D-6/D-14): only expiry + cardholder name; the card number is never editable. -->
            <div v-if="editingId" class="mpm-form" data-testid="pm-edit-form">
                <p class="mpm-form-title">Edit card</p>
                <div class="mpm-grid">
                    <div class="mpm-field">
                        <label>Exp. month</label>
                        <input v-model.number="editMonth" type="number" min="1" max="12" class="mpm-input">
                    </div>
                    <div class="mpm-field">
                        <label>Exp. year</label>
                        <input v-model.number="editYear" type="number" min="2000" max="2100" class="mpm-input">
                    </div>
                    <div class="mpm-field">
                        <label>Cardholder</label>
                        <input v-model="editName" type="text" placeholder="Name on card" class="mpm-input">
                    </div>
                </div>
                <div class="mpm-actions">
                    <PrimeButton label="Save changes" :loading="busy" @click="onSaveEdit" />
                    <PrimeButton label="Cancel" class="p-btn-root_secondary" :disabled="busy" @click="editingId = null" />
                </div>
            </div>

            <!-- Inline add card. -->
            <div v-if="adding" class="mpm-form" data-testid="pm-add-form">
                <p class="mpm-form-title">Add a card</p>
                <div class="mpm-grid">
                    <div class="mpm-field">
                        <label>First name</label>
                        <input v-model="addFirstName" type="text" placeholder="John" class="mpm-input">
                    </div>
                    <div class="mpm-field">
                        <label>Last name</label>
                        <input v-model="addLastName" type="text" placeholder="Doe" class="mpm-input">
                    </div>
                </div>
                <div class="mpm-field">
                    <label>Card details</label>
                    <div id="manage-pm-card-element" class="mpm-card-element"></div>
                    <p v-if="addCardError" class="mpm-error">{{ addCardError }}</p>
                </div>
                <div class="mpm-actions">
                    <PrimeButton label="Add card" :loading="busy" :disabled="!canSubmitNewCard" @click="onSaveNewCard" />
                    <PrimeButton label="Cancel" class="p-btn-root_secondary" :disabled="busy" @click="cancelAdd" />
                </div>
            </div>
        </div>

        <footer class="mpm-footer">
            <PrimeButton label="Done" class="p-btn-root_secondary" @click="closeDialog" />
        </footer>
    </div>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button';
import { CreditCardIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import PaymentMethodsPanel from '~/components/billing/PaymentMethodsPanel.vue';
import { usePaymentMethods, type PaymentMethod } from '~/composables/usePaymentMethods';
import { useAuthStore } from '~/stores/auth';
import { usePersonStore } from '~/stores/person';
import { ModalNames } from '~/types/modals';

const CAP = 5;

const dialogRef = getDialogRef(ModalNames.MANAGE_PAYMENT_METHODS);
const authStore = useAuthStore();
const personStore = usePersonStore();
const config = useRuntimeConfig();
const { showSuccess, showError } = useGlobalToast();

const personId = computed(() => personStore.person?.id);
const { methods, sortedMethods, fetch: fetchPaymentMethods, setPrimary, edit, remove, add } = usePaymentMethods(() => personId.value);
const busy = ref(false);
const atCap = computed(() => methods.value.length >= CAP); // cap check uses the raw list (count only)

const editingId = ref<string | null>(null);
const editMonth = ref<number | null>(null);
const editYear = ref<number | null>(null);
const editName = ref('');

const adding = ref(false);
const addFirstName = ref('');
const addLastName = ref('');
const addCardError = ref<string | null>(null);
const newCardComplete = ref(false);
// Sanctioned countries are not supported for billing (mirrors the retired update-card flow).
const BLOCKED_COUNTRIES = ['RU', 'UA', 'BY'];
const addCardCountry = ref<string | null>(null);
const isBlockedCountry = computed(() => BLOCKED_COUNTRIES.includes(addCardCountry.value ?? ''));
const canSubmitNewCard = computed(
    () => !!addFirstName.value.trim() && !!addLastName.value.trim() && newCardComplete.value && !isBlockedCountry.value,
);

function closeDialog() {
    dialogRef.value.close();
}

async function onSetPrimary(id: string) {
    busy.value = true;
    try {
        await setPrimary(id);
        await authStore.getMe(); // keep the singular pm_* display cache (D-17) in sync
        showSuccess('Success', 'Primary card updated.');
    } catch (e: any) {
        showError('Error', e?.message || 'Could not set primary card.');
    } finally {
        busy.value = false;
    }
}

function onEdit(id: string) {
    const m = methods.value.find((x) => x.id === id);
    if (!m) return;
    editMonth.value = m.exp_month ?? null;
    editYear.value = m.exp_year ?? null;
    editName.value = m.name ?? '';
    // Tear down any in-progress add-card flow (Stripe elements + newCardComplete) before switching
    // to edit — same path as Cancel — so a later re-open of Add can't submit the abandoned
    // SetupIntent/card with a stale "complete" flag.
    cancelAdd();
    editingId.value = id;
}

async function onSaveEdit() {
    if (!editingId.value || busy.value) return;
    const payload: Record<string, unknown> = {};
    if (editMonth.value) payload.exp_month = editMonth.value;
    if (editYear.value) payload.exp_year = editYear.value;
    if (editName.value) payload.cardholder_name = editName.value;
    busy.value = true;
    try {
        await edit(editingId.value, payload);
        showSuccess('Success', 'Card updated.');
        editingId.value = null;
    } catch (e: any) {
        showError('Error', e?.message || 'Could not update card.');
    } finally {
        busy.value = false;
    }
}

// The panel confirms the delete inline (in-row), so this just performs the removal.
async function onDelete(id: string) {
    busy.value = true;
    try {
        await remove(id);
        await authStore.getMe();
        showSuccess('Success', 'Payment method removed.');
    } catch (e: any) {
        showError('Error', e?.message || 'Could not remove payment method.');
    } finally {
        busy.value = false;
    }
}

// ── Inline add-card (Stripe) ──────────────────────────────────────────────
let stripe: any = null;
let elements: any = null;
let cardElement: any = null;
// Bumped on every setupStripeAddForm() so a superseded run (add cancelled then re-opened while the
// first was still awaiting the setup-intent/Stripe import) can detect it lost the race and bow out.
let setupGeneration = 0;

function onAdd() {
    if (atCap.value) return; // panel disables Add at the cap (D-23); guard anyway.
    editingId.value = null;
    addCardError.value = null;
    addFirstName.value = personStore.person?.first_name || '';
    addLastName.value = personStore.person?.last_name || '';
    adding.value = true;
    nextTick(setupStripeAddForm);
}

function cancelAdd() {
    adding.value = false;
    teardownStripeAddForm();
}

async function setupStripeAddForm() {
    addCardError.value = null;
    const generation = ++setupGeneration;
    if (!config.public.pb.stripeKey) {
        addCardError.value = 'Stripe is not configured.';
        return;
    }
    const id = personId.value;
    if (!id) {
        addCardError.value = 'No account loaded.';
        return;
    }

    // Mint an off-session SetupIntent (cap-checked server-side, D-23). Driving the element with
    // its client secret means confirming runs SCA/3DS NOW and authorizes the card for future
    // off-session renewals (W-1) — a plain createPaymentMethod would skip that.
    const { data, status, message } = await usePbApi<{ data: { client_secret: string } }>(
        `/people/${id}/financial/payment-methods/setup-intent`,
        { method: 'POST' },
    );
    // Superseded while awaiting the setup-intent request (cancelled + re-opened) — a newer run
    // already owns the form; don't let this stale error clobber its state.
    if (generation !== setupGeneration) return;
    const clientSecret = data?.data?.client_secret;
    if (status < 200 || status >= 300 || !clientSecret) {
        // Keep the add form open so this error stays visible — addCardError only renders inside the
        // form (v-if="adding"), so hiding the form here would make it vanish with no explanation.
        addCardError.value = message || 'Could not start card setup.';
        return;
    }

    const stripeModule = await import('@stripe/stripe-js');
    const loadedStripe = await stripeModule.loadStripe(config.public.pb.stripeKey);
    // Superseded by a newer setup while awaiting the Stripe import/load → bow out BEFORE touching
    // the shared `stripe` ref. Assigning it here would clobber the newer run's Stripe instance out
    // from under its already-created elements/cardElement, breaking confirmSetup's "same Stripe
    // instance" requirement even though the generation check below would still catch the mount step.
    if (generation !== setupGeneration) return;
    if (!loadedStripe) {
        addCardError.value = 'Could not initialise Stripe.';
        return;
    }
    stripe = loadedStripe;

    // The user may have cancelled/closed the add form while we awaited the setup-intent request and
    // the Stripe import; its mount node is then gone (v-if="adding"). Abort cleanly rather than
    // mounting the Payment Element into a detached selector, which throws.
    if (!adding.value || !document.getElementById('manage-pm-card-element')) {
        teardownStripeAddForm();
        return;
    }

    // Cards + Apple/Google Pay only: the SetupIntent is card-typed (drops US bank account); turn
    // Link off. Apple/Google Pay are card-backed wallets, so 'auto' still surfaces them where the
    // browser/device supports them (Google Pay on Chrome; Apple Pay on Safari w/ domain registered).
    // billingDetails.name is collected by our own fields and supplied at confirm time.
    elements = stripe.elements({ clientSecret });
    cardElement = elements.create('payment', {
        fields: { billingDetails: { name: 'never' } },
        wallets: { googlePay: 'auto', applePay: 'auto', link: 'never' },
    });
    cardElement.mount('#manage-pm-card-element');
    cardElement.on('change', (e: any) => {
        newCardComplete.value = e.complete;
        // Block cards billed to sanctioned countries. The Payment Element surfaces the selected
        // country in its change event; disable submit + surface the reason rather than confirming.
        const country = e?.value?.billingDetails?.address?.country as string | undefined;
        addCardCountry.value = country ?? null;
        addCardError.value = country && BLOCKED_COUNTRIES.includes(country)
            ? 'This country is not supported.'
            : null;
    });
}

function teardownStripeAddForm() {
    cardElement?.unmount?.();
    cardElement = null;
    elements = null;
    stripe = null;
    newCardComplete.value = false;
    addCardCountry.value = null;
}

async function onSaveNewCard() {
    if (!canSubmitNewCard.value || busy.value || !stripe || !elements) return;
    busy.value = true;
    try {
        const { error: submitError } = await elements.submit();
        if (submitError) {
            addCardError.value = submitError.message || 'Card details are incomplete.';
            return;
        }
        // Confirm the SetupIntent — runs 3DS if the issuer requires it, attaches the PM, and records
        // the off-session mandate. redirect:'if_required' keeps the card challenge inline.
        const { error, setupIntent } = await stripe.confirmSetup({
            elements,
            confirmParams: {
                return_url: window.location.href,
                payment_method_data: { billing_details: { name: `${addFirstName.value} ${addLastName.value}`.trim() } },
            },
            redirect: 'if_required',
        });
        if (error) {
            addCardError.value = error.message || 'Could not add card.';
            return;
        }
        const pmId = typeof setupIntent?.payment_method === 'string'
            ? setupIntent.payment_method
            : setupIntent?.payment_method?.id;
        if (!pmId) {
            addCardError.value = 'Could not add card.';
            return;
        }
        // Server-side bookkeeping (cap/dedup/set-primary) + refetch; throws (surfaced here) on 422.
        await add(pmId);
        await authStore.getMe();
        showSuccess('Success', 'Payment method added.');
        cancelAdd();
    } catch (e: any) {
        addCardError.value = e?.message || 'Could not add card.';
    } finally {
        busy.value = false;
    }
}

onMounted(() => {
    fetchPaymentMethods();
    // Opened via the "Add payment method" button (no card on file) → jump straight to the form.
    if (dialogRef.value?.data?.openAdd) onAdd();
});
onBeforeUnmount(teardownStripeAddForm);
</script>

<!-- Plain CSS (hex), independent of hive's custom Tailwind palette (which omits some shades). -->
<style scoped>
.manage-pm-modal {
    width: 91.6667%;
    margin: 0 auto;
    background: #fff;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
}
@media (min-width: 640px) {
    .manage-pm-modal { width: 100%; max-width: 36rem; }
}
.mpm-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 24px 24px 16px;
}
.mpm-title { display: flex; align-items: flex-start; gap: 12px; }
.mpm-icon {
    display: flex;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    background: #f4f4f5;
}
.mpm-heading { color: #09090b; font-size: 18px; font-weight: 600; line-height: 1.6; margin: 0; }
.mpm-sub { color: #52525b; font-size: 14px; line-height: 1.4; margin: 2px 0 0; }
.mpm-close {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    color: #a1a1aa;
    cursor: pointer;
    flex-shrink: 0;
    background: transparent;
    border: none;
}
.mpm-close:hover { color: #52525b; }
.mpm-body { display: flex; flex-direction: column; gap: 16px; padding: 0 24px; }
.mpm-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 4px;
    padding: 16px;
    border-radius: 10px;
    border: 1px solid #e4e4e7;
    background: #fafafa;
}
.mpm-form-title { font-size: 14px; font-weight: 600; color: #27272a; margin: 0; }
.mpm-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
@media (min-width: 640px) {
    .mpm-grid { grid-template-columns: repeat(3, 1fr); }
}
.mpm-field { display: flex; flex-direction: column; gap: 4px; }
.mpm-field label { color: #27272a; font-size: 14px; }
.mpm-input {
    width: 100%;
    border: 1px solid #d4d4d8;
    border-radius: 6px;
    background: #fff;
    padding: 8px 12px;
    font-size: 14px;
    color: #18181b;
}
.mpm-input:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 1px #2563eb; }
.mpm-card-element {
    min-height: 44px;
    border: 1px solid #d4d4d8;
    border-radius: 6px;
    background: #fff;
    padding: 8px 12px;
}
.mpm-error { font-size: 12px; color: #dc2626; margin: 4px 0 0; }
.mpm-actions { display: flex; gap: 8px; justify-content: flex-end; }
.mpm-footer { display: flex; justify-content: flex-end; padding: 16px 24px 24px; }
</style>
